import * as ko from "knockout";
import * as localisationHelper from 'localisationHelper';
import * as globalConfig from "globalConfig";
import * as amplify from "amplify";
import * as moment from "moment";
import * as $ from "jquery";

// Helpers
import "knockoutCustomBindings";

// Services
import { UniversalBasketService } from 'Content/ts/UniversalBasket/Services/universalBasketService';

export interface UniversalBasketTimerParams {
    Id: string,
    RefreshPageWhenExpiryModalDismissed: boolean,
    InHeader: boolean,
    SecondsUntilExpiry: KnockoutObservable<number>;
}

export default class UniversalBasketTimer {
    hasExpired: KnockoutObservable<boolean>;

    /** Total number of seconds until basket expires. */
    secondsUntilExpiry: KnockoutObservable<number>;

    /** Remaining time formatted for display. */
    formattedTimeRemaining: KnockoutObservable<string>;

    /** Minutes component of time remaining (0-59). */
    minutesRemaining: KnockoutObservable<number>;

    /** Seconds component of time remaining (0-59). */
    secondsRemaining: KnockoutObservable<number>;

    /** Whether screen reader should announce the current time remaining (true at hard coded intervals). */
    screenReaderAnnounceTimeRemaining: KnockoutComputed<boolean>;

    /** Screen reader friendly time remaining. */
    screenReaderFormattedTimeRemaining: KnockoutComputed<string>;

    /** Populates the id attribute, used to differentiate between multiple timers on a page. */
    id: string;

    /** Whether the component is being shown in the page header. Used for styling differences. */
    inHeader: boolean;

    /** Basket expired modal */
    modalDismissed: KnockoutObservable<boolean>;
    showModal: KnockoutComputed<boolean>;
    refreshPageWhenExpiryModalDismissed: boolean;

    customMessages: {
        basketReservedText: string;
        basketTimerExpiredText: string;
        basketMinutesSecondsText: string;
        basketMinutesText: string;
        basketExpiredText: string;
        basketExpiredMessageText: string;
    }

    constructor(params: UniversalBasketTimerParams) {
        this.setupCustomMessages();

        if (params.SecondsUntilExpiry == null) {
            this.secondsUntilExpiry = ko.observable(null);

            /** Listen for events that should update the timer. */
            amplify.subscribe(globalConfig.AmplifyEvents.Names.CountBasketItems, function () {
                this.getBasketCountAndExpiry();
            }.bind(this));

        } else {
            this.secondsUntilExpiry = params.SecondsUntilExpiry;
        }

        this.formattedTimeRemaining = ko.observable("--:--");
        this.minutesRemaining = ko.observable(0);
        this.secondsRemaining = ko.observable(0);
        this.hasExpired = ko.observable(false);
        this.modalDismissed = ko.observable(false);
        this.id = params.Id ?? "basket-timer-" + crypto.randomUUID();
        this.refreshPageWhenExpiryModalDismissed = params.RefreshPageWhenExpiryModalDismissed ?? false;
        this.inHeader = params.InHeader ?? false;
        this.setupComputeds();
        this.initialise();
    }

    private initialise(): void {
        /** If the value for the seconds remaining was not passed into the component we need to get it here. */
        if (this.secondsUntilExpiry() === null) {
            this.getBasketCountAndExpiry().finally(() => {
                this.initTimerUpdate();
            });
        } else {
            this.initTimerUpdate();
        }
    }

    private getBasketCountAndExpiry(): Promise<void> {
        return UniversalBasketService.getBasketCountAndExpiryAsync().then((result) => {
            this.secondsUntilExpiry(result.SecondsUntilExpiry);
        });
    }

    private initTimerUpdate() {
        const interval = setInterval(() => {
            this.updateTimerText(interval);
        }, 1000);
    }

    private updateTimerText(interval: number): void {
        this.secondsUntilExpiry(this.secondsUntilExpiry() - 1);
        const now = moment();
        const basketExpiresAt = moment().add(this.secondsUntilExpiry(), 'seconds');
        const diff = basketExpiresAt.diff(now);

        if (diff <= 0) {
            clearInterval(interval);
            this.hasExpired(true);

            // Hack to prevent the backdrop from covering the dialog box.
            if (this.inHeader) {
                $('.modal-backdrop').appendTo('#basket-timer-modal');
            }

        } else {
            const diffMoment = moment.utc(diff);
            this.formattedTimeRemaining(diffMoment.format("mm:ss"));
            this.minutesRemaining(diffMoment.minutes());
            this.secondsRemaining(diffMoment.seconds());
        }
    }

    private setupCustomMessages() {
        this.customMessages = {
            basketReservedText: localisationHelper.getString('BasketResources', 'BasketTimerReservedFor'),
            basketTimerExpiredText: localisationHelper.getString('BasketResources', 'BasketTimerExpired'),
            basketMinutesSecondsText: localisationHelper.getString('BasketResources', 'BasketTimerMinutesAndSeconds'),
            basketMinutesText: localisationHelper.getString('BasketResources', 'BasketTimerMinutes'),
            basketExpiredText: localisationHelper.getString('BasketResources', 'BasketExpired'),
            basketExpiredMessageText: localisationHelper.getString('BasketResources', 'BasketExpiredMessage'),
        }
    }

    private setupComputeds() {
        this.screenReaderAnnounceTimeRemaining = ko.pureComputed(() => {

            // Suppress announcements if not visible on page (for mobile vs full view).
            if (this.id && !$("#" + this.id).is(":visible")) {
                return false;
            }

            if (this.minutesRemaining() === 0 && this.secondsRemaining() === 0) {
                return false;
            }

            let frequency;

            if (this.minutesRemaining() >= 5) {
                frequency = 60;
            } else if (this.minutesRemaining() >= 1) {
                frequency = 30;
            } else {
                frequency = 10;
            }

            return this.secondsRemaining() % frequency === 0 && !this.hasExpired();
        });

        this.screenReaderFormattedTimeRemaining = ko.pureComputed(() => {
            if (this.secondsRemaining() === 0) {
                return this.customMessages.basketMinutesText.replace("{0}", this.minutesRemaining().toString());
            }
            return this.customMessages.basketMinutesSecondsText.replace("{0}", this.minutesRemaining().toString()).replace("{1}", this.secondsRemaining().toString());
        });

        this.showModal = ko.pureComputed(() => {
            return this.hasExpired() && !this.modalDismissed();
        });
    }
        
    private closeModal() {
        if (this.refreshPageWhenExpiryModalDismissed) {
            window.location.reload();
            return;
        }
        this.modalDismissed(true);
        amplify.publish(globalConfig.AmplifyEvents.Names.CountBasketItems);
    }
}

const componentName = "universal-basket-timer";
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: UniversalBasketTimer,
    template: { require: `text!/Themes/Default/Content/ts/UniversalBasket/${componentName}.html` }
});
